<script setup lang="ts">
import { reactive, ref, watch } from 'vue'
import { call, identity, type Schemas } from '../api/http'
import { useSession } from '../stores/session'
import { compact, useForm } from '../lib/form'
import UiCard from '../components/UiCard.vue'
import UiButton from '../components/UiButton.vue'
import UiField from '../components/UiField.vue'
import ErrorBanner from '../components/ErrorBanner.vue'
import LegalFields from '../components/LegalFields.vue'

const session = useSession()
const { busy, error, submit, fieldErr } = useForm()
const saved = ref(false)
const form = reactive({
  name: '',
  legal: { legal_name: '', reg_number: '', address: '', vat_number: '', iban: '', bank_name: '' } as Schemas['Legal'],
})

watch(
  () => session.workspace,
  (w) => {
    if (!w) return
    form.name = w.name
    Object.assign(form.legal, { vat_number: '', iban: '', bank_name: '' }, w.legal)
  },
  { immediate: true },
)

async function onSave() {
  saved.value = false
  const wid = session.workspace!.id
  saved.value = await submit(async () => {
    const w = await call(
      identity.PATCH('/workspaces/{workspace_id}', {
        params: { path: { workspace_id: wid } },
        body: { name: form.name, legal: compact(form.legal) },
      }),
    )
    session.workspace = w
    session.workspaces = session.workspaces.map((x) => (x.id === w.id ? w : x))
  })
}
</script>

<template>
  <div class="page">
    <header class="page-header">
      <div>
        <h1>{{ $t('company.title') }}</h1>
        <p class="muted">{{ $t('company.subtitle') }}</p>
      </div>
    </header>
    <UiCard>
      <form class="stack" @submit.prevent="onSave">
        <p v-if="!session.canAdmin" class="muted small">{{ $t('company.readOnly') }}</p>
        <ErrorBanner :error="error" />
        <UiField :label="$t('fields.workspaceName')" :error="fieldErr('name')">
          <input v-model.trim="form.name" name="name" required maxlength="100" :disabled="!session.canAdmin" :aria-invalid="!!fieldErr('name')" />
        </UiField>
        <LegalFields v-model="form.legal" :field-err="fieldErr" :disabled="!session.canAdmin" />
        <div v-if="session.canAdmin" class="row">
          <UiButton type="submit" :loading="busy">{{ $t('common.save') }}</UiButton>
          <span v-if="saved" class="small" style="color: var(--gain)" role="status">{{ $t('common.saved') }}</span>
        </div>
      </form>
    </UiCard>
  </div>
</template>
